import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthFormData, AuthErrors } from '../types';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

interface AuthFormProps {
  mode: 'login' | 'register';
  formData: AuthFormData;
  errors: AuthErrors;
  isLoading: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onModeToggle: () => void;
  successMessage?: string;
  // Phone OTP props
  phoneVerified?: boolean;
  phoneOtpSent?: boolean;
  onSendPhoneOtp?: () => void;
  onVerifyPhoneOtp?: () => void;
  sendingOtp?: boolean;
  verifyingOtp?: boolean;
  // Email OTP props
  emailVerified?: boolean;
  emailOtpSent?: boolean;
  onSendEmailOtp?: () => void;
  onVerifyEmailOtp?: () => void;
  sendingEmailOtp?: boolean;
  verifyingEmailOtp?: boolean;
}

const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  formData,
  errors,
  isLoading,
  onInputChange,
  onSubmit,
  onModeToggle,
  successMessage,
  phoneVerified = false,
  phoneOtpSent = false,
  onSendPhoneOtp,
  onVerifyPhoneOtp,
  sendingOtp = false,
  verifyingOtp = false,
  emailVerified = false,
  emailOtpSent = false,
  onSendEmailOtp,
  onVerifyEmailOtp,
  sendingEmailOtp = false,
  verifyingEmailOtp = false
}) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={20} className="text-green-600" />
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Registration Fields */}
      {mode === 'register' && (
        <>
          <Input
            label="Full Name"
            type="text"
            name="fullName"
            placeholder="Enter your full name"
            value={formData.fullName || ''}
            onChange={onInputChange}
            error={errors.fullName}
            required
            disabled={isLoading}
            className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313]"
          />

          {formData.role === 'brand' && (
            <Input
              label="Company Name"
              type="text"
              name="companyName"
              placeholder="Enter your company name"
              value={formData.companyName || ''}
              onChange={onInputChange}
              error={errors.companyName}
              required
              disabled={isLoading}
              className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313]"
            />
          )}
        </>
      )}

      {/* Email Field - With OTP verification for registration */}
      {mode === 'register' ? (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={onInputChange}
                error={errors.email}
                required
                disabled={isLoading || emailVerified}
                autoComplete="email"
                className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313]"
              />
            </div>
            {!emailVerified && (
              <button
                type="button"
                onClick={onSendEmailOtp}
                disabled={isLoading || sendingEmailOtp || !formData.email || emailVerified}
                className="mt-7 px-4 py-2 h-[42px] bg-dark-700 text-white text-sm font-medium rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                {sendingEmailOtp ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Sending...
                  </span>
                ) : emailOtpSent ? 'Resend OTP' : 'Send OTP'}
              </button>
            )}
            {emailVerified && (
              <div className="mt-7 px-4 py-2 h-[42px] bg-green-600 text-white text-sm font-medium rounded-lg flex items-center gap-2">
                <Icon name="CheckCircle" size={14} />
                Verified
              </div>
            )}
          </div>

          {/* Email OTP Input Field */}
          {emailOtpSent && !emailVerified && (
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  label="Enter Email OTP"
                  type="text"
                  name="emailOtp"
                  placeholder="Enter 6-digit OTP"
                  value={formData.emailOtp || ''}
                  onChange={onInputChange}
                  error={errors.emailOtp}
                  maxLength={6}
                  required
                  disabled={isLoading || verifyingEmailOtp}
                  autoComplete="one-time-code"
                  className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313]"
                />
              </div>
              <button
                type="button"
                onClick={onVerifyEmailOtp}
                disabled={isLoading || verifyingEmailOtp || !formData.emailOtp || formData.emailOtp.length !== 6}
                className="mt-7 px-4 py-2 h-[42px] bg-[#E42313] text-white text-sm font-medium rounded-lg hover:bg-[#C41F0F] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {verifyingEmailOtp ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Verifying...
                  </span>
                ) : 'Verify'}
              </button>
            </div>
          )}

          {emailOtpSent && !emailVerified && (
            <p className="text-xs text-gray-400">
              A 6-digit OTP has been sent to your email. Check your inbox.
            </p>
          )}
        </div>
      ) : (
        /* Login - Simple email field */
        <Input
          label="Email Address"
          type="email"
          name="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={onInputChange}
          error={errors.email}
          required
          disabled={isLoading}
          autoComplete="email"
          className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313]"
        />
      )}

      {/* Password Field */}
      <div className="relative">
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={onInputChange}
          error={errors.password}
          required
          disabled={isLoading}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313] pr-10"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-gray-400 hover:text-black transition-all"
          disabled={isLoading}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>

      {/* Confirm Password Field (Register Only) */}
      {mode === 'register' && (
        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            placeholder="Confirm your password"
            value={formData.confirmPassword || ''}
            onChange={onInputChange}
            error={errors.confirmPassword}
            required
            disabled={isLoading}
            autoComplete="new-password"
            className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313] pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-gray-400 hover:text-black transition-all"
            disabled={isLoading}
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>
      )}

      {/* Phone Number (Optional) */}
      {mode === 'register' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                label="Phone Number (Optional)"
                type="tel"
                name="phoneNumber"
                placeholder="+91 9876543210"
                value={formData.phoneNumber || ''}
                onChange={onInputChange}
                error={errors.phoneNumber}
                disabled={isLoading || phoneVerified}
                className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313]"
              />
            </div>
            {formData.phoneNumber && !phoneVerified && (
              <button
                type="button"
                onClick={onSendPhoneOtp}
                disabled={isLoading || sendingOtp || !formData.phoneNumber || phoneVerified}
                className="mt-7 px-4 py-2 h-[42px] bg-dark-700 text-white text-sm font-medium rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                {sendingOtp ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Sending...
                  </span>
                ) : phoneOtpSent ? 'Resend OTP' : 'Verify (Optional)'}
              </button>
            )}
            {phoneVerified && (
              <div className="mt-7 px-4 py-2 h-[42px] bg-green-600 text-white text-sm font-medium rounded-lg flex items-center gap-2">
                <Icon name="CheckCircle" size={14} />
                Verified
              </div>
            )}
          </div>

          {/* OTP Input Field - Show only when OTP is sent and not verified */}
          {phoneOtpSent && !phoneVerified && (
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  label="Enter OTP"
                  type="text"
                  name="phoneOtp"
                  placeholder="Enter 6-digit OTP"
                  value={formData.phoneOtp || ''}
                  onChange={onInputChange}
                  error={errors.phoneOtp}
                  maxLength={6}
                  required
                  disabled={isLoading || verifyingOtp}
                  className="border-gray-300 focus:border-[#E42313] focus:ring-[#E42313]"
                />
              </div>
              <button
                type="button"
                onClick={onVerifyPhoneOtp}
                disabled={isLoading || verifyingOtp || !formData.phoneOtp || formData.phoneOtp.length !== 6}
                className="mt-7 px-4 py-2 h-[42px] bg-[#E42313] text-white text-sm font-medium rounded-lg hover:bg-[#C41F0F] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {verifyingOtp ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Verifying...
                  </span>
                ) : 'Verify'}
              </button>
            </div>
          )}

          {phoneOtpSent && !phoneVerified && (
            <p className="text-xs text-gray-400">
              A 6-digit OTP has been sent to your phone. Enter it above to verify.
            </p>
          )}
        </div>
      )}

      {/* Error Message */}
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={18} className="text-[#E42313] mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#E42313]">{errors.submit}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        className="bg-[#E42313] hover:bg-[#C41F0F] text-white font-semibold py-3 rounded-xl transition-all shadow-lg hover:shadow-xl"
      >
        {mode === 'login' ? 'Sign In' : 'Create Account'}
      </Button>

      {/* Forgot Password (Login Only) */}
      {mode === 'login' && (
        <div className="flex items-center justify-start">
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-sm font-medium text-[#E42313] hover:underline transition-all"
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>
      )}

      {/* Mode Toggle */}
      <div className="text-center pt-2">
        <button
          type="button"
          onClick={onModeToggle}
          className="text-sm font-medium text-gray-600 hover:text-black transition-all"
          disabled={isLoading}
        >
          {mode === 'login' ? "Don't have an account? " 
            : 'Already have an account? '}
          <span className="text-[#E42313] hover:underline">
            {mode === 'login' ? 'Sign up' : 'Sign in'}
          </span>
        </button>
      </div>
    </form>
  );
};

export default AuthForm;